/**
 * API client for CountriesDB API
 */
export class CountriesDBClient {
    /**
     * Fetch countries from the API
     */
    static async fetchCountries(options) {
        const { apiKey, backendUrl, shouldUseGeoIP = true, isoCountryNames = false, languageHeaders = {}, } = options;
        const base = `${backendUrl}/api/countries`;
        const params = [];
        if (!shouldUseGeoIP) {
            params.push('no_geoip=1');
        }
        if (isoCountryNames) {
            params.push('country_name_source=iso');
        }
        const url = params.length ? `${base}?${params.join('&')}` : base;
        return this.fetchFromApi(url, apiKey, languageHeaders);
    }
    /**
     * Fetch subdivisions for a specific country
     */
    static async fetchSubdivisions(options) {
        const { apiKey, backendUrl, countryCode, shouldUseGeoIP = true, preferOfficial = false, subdivisionRomanizationPreference, preferLocalVariant = false, languageHeaders = {}, } = options;
        if (!countryCode) {
            return { data: [], language: null };
        }
        const base = `${backendUrl}/api/countries/${countryCode}/subdivisions`;
        const params = [];
        if (!shouldUseGeoIP) {
            params.push('no_geoip=1');
        }
        if (subdivisionRomanizationPreference) {
            params.push(`subdivision_romanization_preference=${encodeURIComponent(subdivisionRomanizationPreference)}`);
        }
        if (preferLocalVariant) {
            params.push('prefer_local_variant=1');
        }
        if (preferOfficial) {
            params.push('prefer_official=1');
        }
        const url = params.length ? `${base}?${params.join('&')}` : base;
        return this.fetchFromApi(url, apiKey, languageHeaders);
    }
    /**
     * Generic API fetch method
     */
    static async fetchFromApi(url, apiKey, languageHeaders) {
        try {
            const response = await fetch(url, {
                headers: {
                    ...languageHeaders,
                    'X-API-KEY': apiKey,
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                let errorMessage = `HTTP Error: ${response.status}`;
                try {
                    const errorData = await response.json();
                    if (errorData.error) {
                        errorMessage = errorData.error;
                    }
                }
                catch {
                    // Ignore JSON parse errors
                }
                throw new Error(errorMessage);
            }
            const data = (await response.json());
            const language = response.headers.get('X-Selected-Language') || null;
            return { data, language };
        }
        catch (error) {
            console.error(`Failed to fetch data from ${url}:`, error);
            throw error;
        }
    }
    /**
     * Get language headers from browser and config
     */
    static getLanguageHeaders(forcedLanguage, defaultLanguage) {
        const headers = {};
        if (forcedLanguage) {
            headers['X-Forced-Language'] = forcedLanguage;
        }
        if (defaultLanguage) {
            headers['X-Default-Language'] = defaultLanguage;
        }
        // Use browser's language preference
        if (typeof navigator !== 'undefined' && navigator.language) {
            headers['Accept-Language'] = navigator.language;
        }
        else {
            headers['Accept-Language'] = 'en';
        }
        return headers;
    }
}
